import React, { ReactElement } from "react";
import styled, { css } from "styled-components";
import { Radius, content } from "@glossy";
import Tooltip from "@ui-kit/tooltip";
import Picture from "@ui-kit/utils/image";
import { ClusterPost } from "@radarly/models/posts";
import { PICTURE_DETAIL_WIDTH, PICTURE_GRID_WIDTH } from "./dimensions";

export interface IProps {
  post: ClusterPost;
  display: "row" | "grid";
  isFullscreen?: boolean;
  detailImageFullscreenSize?: number;
  gridImageFullscreenSize?: number;
  onClick?: () => void;
  renderTooltip?: () => ReactElement;
}

const PictureWrapper = styled.div<{
  display: IProps["display"];
  isFullscreen: boolean;
  detailImageFullscreenSize: number;
  gridImageFullscreenSize: number;
}>`
  cursor: pointer;
  background-color: ${(props) => content(props, "tertiary")};
  position: relative;
  ${({
    display,
    isFullscreen,
    detailImageFullscreenSize,
    gridImageFullscreenSize,
  }) =>
    display === "grid"
      ? css`
          display: grid;
          place-items: center;
          position: relative;
          overflow: hidden;
          border-radius: ${Radius.Small};
          background-color: ${(props) => content(props, "tertiary")};
          color: ${(props) => content(props, "secondary")};
          width: 100%;
          height: ${isFullscreen
            ? `${detailImageFullscreenSize}px`
            : `${PICTURE_DETAIL_WIDTH}px`};
          &::before {
            // for aspect ratio
            content: "";
            display: block;
            padding-bottom: 100%;
            grid-area: 1 / 1 / 2 / 2;
          }
        `
      : css`
          display: flex;
          align-items: center;
          justify-content: center;
          height: ${isFullscreen
            ? `${gridImageFullscreenSize}px`
            : `${PICTURE_GRID_WIDTH}px`};
          width: ${isFullscreen
            ? `${gridImageFullscreenSize}px`
            : `${PICTURE_GRID_WIDTH}px`};
        `}
  & > * {
    position: absolute;
  }
`;

const TooltipWrapper = ({ condition, wrapper, children }): ReactElement =>
  condition ? wrapper(children) : children;

const ImageClustersGrid = ({
  post,
  onClick,
  display,
  isFullscreen,
  detailImageFullscreenSize,
  gridImageFullscreenSize,
  renderTooltip,
}: IProps) => {
  return (
    <TooltipWrapper
      condition={!!renderTooltip}
      wrapper={(children) => (
        <Tooltip tooltip={renderTooltip()}>{children}</Tooltip>
      )}
    >
      <PictureWrapper
        key={post.uid}
        display={display}
        onClick={onClick}
        isFullscreen={isFullscreen}
        detailImageFullscreenSize={detailImageFullscreenSize}
        gridImageFullscreenSize={gridImageFullscreenSize}
      >
        <Picture
          key={post.uid}
          image={post.mediaUrl}
          fallbackImage={post.srcFallback}
          lazy
          animated
        />
      </PictureWrapper>
    </TooltipWrapper>
  );
};

export default ImageClustersGrid;
